import type { CustomerOrder } from '../../customerOrdersApi'
import type { CustomerPrivacyDashboard } from '../../customerPrivacyApi'
import type { CustomerSupportRequest } from '../../customerSupportApi'
import type { CustomerAccount, CustomerProfile } from '../../types'
import type { OrderProgressStep } from './dashboardTypes'

export const joinClassNames = (...classNames: Array<string | null | undefined | false>) =>
  classNames.filter(Boolean).join(' ')

export const toTimestamp = (value: string | null | undefined) => {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

export const formatCount = (value: number, locale: string) => new Intl.NumberFormat(locale).format(value)

export const getAddressCount = (profile: CustomerProfile) =>
  [profile.residentialAddress, profile.billingAddress, profile.shippingAddress].filter((value) =>
    value.trim().length > 0,
  ).length

export const resolveAccountStatusLabel = (
  status: CustomerAccount['accountStatus'] | CustomerPrivacyDashboard['account']['accountStatus'] | undefined,
  language: 'it' | 'en',
) => {
  const labels =
    language === 'it'
      ? {
          active: 'Attivo',
          pending_deletion: 'Eliminazione richiesta',
          anonymized: 'Anonimizzato',
          deleted: 'Eliminato',
        }
      : {
          active: 'Active',
          pending_deletion: 'Deletion requested',
          anonymized: 'Anonymized',
          deleted: 'Deleted',
        }

  if (!status) return language === 'it' ? 'Non disponibile' : 'Unavailable'
  return labels[status] ?? status
}

export const buildOrderProgressSteps = (order: CustomerOrder, language: 'it' | 'en'): OrderProgressStep[] => {
  const cancelledSteps: OrderProgressStep[] = [
    {
      id: 'ordered',
      label: language === 'it' ? 'Ordine ricevuto' : 'Order received',
      tone: 'done',
    },
    {
      id: 'cancelled',
      label: language === 'it' ? 'Ordine annullato' : 'Order cancelled',
      tone: 'cancelled',
    },
  ]

  if (order.status === 'cancelled') {
    return cancelledSteps
  }

  const baseSteps: OrderProgressStep[] = [
    {
      id: 'ordered',
      label: language === 'it' ? 'Ordine ricevuto' : 'Order received',
      tone: 'upcoming',
    },
    {
      id: 'payment',
      label: language === 'it' ? 'Pagamento confermato' : 'Payment confirmed',
      tone: 'upcoming',
    },
    {
      id: 'preparation',
      label: language === 'it' ? 'In preparazione' : 'Preparing',
      tone: 'upcoming',
    },
    {
      id: 'shipping',
      label: language === 'it' ? 'Spedito / In consegna' : 'Shipped / Out for delivery',
      tone: 'upcoming',
    },
  ]

  const currentStepIndex = order.status === 'created' ? 0 : order.status === 'paid' ? 2 : 3

  return baseSteps.map((step, index) => ({
    ...step,
    tone: index < currentStepIndex ? 'done' : index === currentStepIndex ? 'current' : 'upcoming',
  }))
}

export const toOrderStateDescription = (order: CustomerOrder, language: 'it' | 'en') => {
  if (order.status === 'cancelled') {
    return language === 'it'
      ? 'Questo ordine e stato annullato. Se hai bisogno di supporto, apri una richiesta assistenza.'
      : 'This order has been cancelled. If needed, open a support request for assistance.'
  }

  if (order.status === 'shipped') {
    return order.trackingCode || order.trackingUrl
      ? language === 'it'
        ? 'Ordine affidato al corriere. Usa il tracking per seguire gli aggiornamenti di consegna.'
        : 'Order handed to the courier. Use tracking to follow delivery updates.'
      : language === 'it'
        ? 'Ordine spedito. Il tracking verra mostrato appena disponibile dal corriere.'
        : 'Order shipped. Tracking details will appear as soon as available from the courier.'
  }

  if (order.status === 'paid') {
    return language === 'it'
      ? 'Pagamento confermato. Il team sta preparando il pacco per la spedizione.'
      : 'Payment confirmed. The team is preparing your package for shipment.'
  }

  return language === 'it'
    ? 'Ordine ricevuto correttamente. Verifica pagamento e preparazione in corso.'
    : 'Order successfully received. Payment verification and preparation are in progress.'
}

export const toneToStepClasses: Record<OrderProgressStep['tone'], { dot: string; title: string; line: string }> = {
  done: {
    dot: 'border-(--color-status-success-border) bg-(--color-status-success)',
    title: 'text-(--color-status-success)',
    line: 'bg-(--color-status-success-border)',
  },
  current: {
    dot: 'border-(--color-action-primary) bg-(--color-action-primary) ring-4 ring-[color-mix(in_srgb,var(--color-focus-ring)_30%,transparent)]',
    title: 'font-semibold text-(--color-text-primary)',
    line: 'bg-(--color-action-primary)',
  },
  upcoming: {
    dot: 'border-(--color-border-strong) bg-(--color-bg-card)',
    title: 'text-(--color-text-muted)',
    line: 'bg-(--color-border-subtle)',
  },
  cancelled: {
    dot: 'border-(--color-status-error-border) bg-(--color-status-error)',
    title: 'text-(--color-status-error)',
    line: 'bg-(--color-status-error-border)',
  },
}

export const toActivityPrivacyLabel = (
  status: CustomerPrivacyDashboard['requests'][number]['status'],
  language: 'it' | 'en',
) => {
  if (language === 'it') {
    if (status === 'completed') return 'Completata'
    if (status === 'processing') return 'In elaborazione'
    if (status === 'rejected') return 'Rifiutata'
    if (status === 'expired') return 'Scaduta'
    return 'In attesa'
  }

  if (status === 'completed') return 'Completed'
  if (status === 'processing') return 'Processing'
  if (status === 'rejected') return 'Rejected'
  if (status === 'expired') return 'Expired'
  return 'Pending'
}

export const getOpenTicketsCount = (supportRequests: CustomerSupportRequest[]) =>
  supportRequests.filter((request) => request.status === 'open' || request.status === 'in_progress').length
